import { useGetVariantKey } from '@upright/ui/api/common/hooks';
import { ComparisonRef } from '@upright/ui/common/upright/model/output/scores/comparison/types';
import { getVersionAndRelease } from '@upright/ui/untidy/redux/selectors';
import { createContext, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ModelEntityWithScores } from '@upright/common/model/output/scores/model-entity-data/types';
import { RenderOptions, RenderViewComponentParams } from '@upright/common/renderer/types';
import { useIsTeam } from '../../../common/generic/hooks';
import { useGetComparisonDataQuery } from '../../../common/upright/model/output/scores/comparison/context/use-comparison-items/use-get-comparison-data-query';
import { getComponentsToRender, shouldFetchData } from '../utils';
import { useRenderViewOptions } from '../utils/use-render-view-options';

interface RenderViewContext {
  options: Partial<RenderOptions>;
  setOptions: (options: Partial<RenderOptions>) => void;
  componentParams: RenderViewComponentParams[];

  // TODO OA: Drop the two properties below, and change to load data with ReactQuery
  data?: ModelEntityWithScores[];
  getNameOfFirstItem: () => string | undefined;
}

export function useRenderViewContextBuilder(
  initialOptions?: Partial<RenderOptions>,

  // TODO OA: Drop the two param below, and change to load data with ReactQuery
  dataProp?: ModelEntityWithScores[],
): RenderViewContext {
  const [options, setOptions] = useRenderViewOptions(initialOptions);

  const items = options.items;

  const shouldFetch = !dataProp || shouldFetchData(options);

  /**
   * This is an old logic to support earlier data fetching pattern, where RenderView had centralized
   * score fetching. Now that we can require different kinds of data for different components, each
   * component should handle their own data fetching instead.
   *
   * TODO: Migrate old render view components to handle their own data fetching with ReactQuery.
   */
  const variantKey = useGetVariantKey();
  const { version, release } = useSelector(getVersionAndRelease);
  const dataRequest = useGetComparisonDataQuery(
    {
      payload: {
        entities: (items || []).map(item => ({ id: item.id, type: item.type }) satisfies ComparisonRef),
        version,
        release,
      },
      options: variantKey,
    },
    { enabled: shouldFetch },
  );

  const data = dataProp || dataRequest.data;

  const isTeam = useIsTeam();
  const componentParams = getComponentsToRender(options, isTeam);

  // This is also deprecated stuff, see comment above.
  const getNameOfFirstItem = useMemo(() => {
    return () => data?.[0]?.name;
  }, [data]);

  return {
    options,
    setOptions,
    data,
    componentParams,
    getNameOfFirstItem,
  };
}

const ctx = createContext({} as RenderViewContext);

export function useRenderViewContext() {
  return useContext(ctx);
}

export const RenderViewContextProvider = ctx.Provider;
